import { useEffect, useState } from "react";

import { useTheme } from "@/theme/ThemeContext";

export type WTMRiskLevel =
  | "highest"
  | "high"
  | "medium"
  | "low"
  | "unknown"
  | "removed";

export const riskLevelLabel: Record<WTMRiskLevel, string> = {
  highest: "Highest",
  high: "High",
  medium: "Medium",
  low: "Low",
  unknown: "Unknown",
  removed: "Removed",
};

// CSS variables that back the severity palette. The light-mode values are
// defined under `:root` in front/src/tailwind.css; the `.dark` block on the
// same file overrides each one for dark mode. Keeping the chart/inline-style
// colors as CSS-var reads (rather than baked hex) is what lets them pick up
// the dark-mode overrides at runtime.
const RISK_LEVEL_CSS_VAR: Record<WTMRiskLevel, string> = {
  highest: "--color-red-700",
  high: "--color-red-500",
  medium: "--color-yellow-500",
  low: "--color-green-700",
  unknown: "--color-gray-400",
  removed: "--color-gray-100",
};

function readToken(name: string): string {
  if (typeof document === "undefined") return "";
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
}

function readRiskColors(): Record<WTMRiskLevel, string> {
  return {
    highest: readToken(RISK_LEVEL_CSS_VAR.highest),
    high: readToken(RISK_LEVEL_CSS_VAR.high),
    medium: readToken(RISK_LEVEL_CSS_VAR.medium),
    low: readToken(RISK_LEVEL_CSS_VAR.low),
    unknown: readToken(RISK_LEVEL_CSS_VAR.unknown),
    removed: readToken(RISK_LEVEL_CSS_VAR.removed),
  };
}

// Returns the severity palette as resolved hex strings, re-reading the
// underlying CSS variables whenever the theme changes. Use this in components
// (charts, inline-style swatches) instead of a static `Record` so dark-mode
// switches are picked up without the chart having to be remounted.
//
// Note: the initial render reads CSS values before ThemeProvider's effect has
// applied the `.dark` class on initial dark-mode page loads, so the first
// frame may show light-mode colors. This matches the existing ThemeProvider
// pattern (no synchronous class application) and corrects on the next tick.
export function useRiskLevelColors(): Record<WTMRiskLevel, string> {
  const { theme } = useTheme();
  const [colors, setColors] =
    useState<Record<WTMRiskLevel, string>>(readRiskColors);
  useEffect(() => {
    setColors(readRiskColors());
  }, [theme]);
  return colors;
}
